import type { ZeroLogBuffer, ZeroLogLevel } from "./zero-log-buffer"
import { createZeroLogBuffer } from "./zero-log-buffer"

export interface ZeroLogSink {
	log: (
		level: ZeroLogLevel,
		context: Record<string, unknown> | undefined,
		...args: unknown[]
	) => void
}

export interface ZeroConsoleLike {
	error: (...args: unknown[]) => void
	warn: (...args: unknown[]) => void
	info: (...args: unknown[]) => void
	debug: (...args: unknown[]) => void
}

function defaultConsole(): ZeroConsoleLike {
	return {
		error: (...args) => console.error(...args),
		warn: (...args) => console.warn(...args),
		info: (...args) => console.info(...args),
		debug: (...args) => console.debug(...args),
	}
}

const LEVEL_RANK: Record<ZeroLogLevel, number> = {
	debug: 0,
	info: 1,
	warn: 2,
	error: 3,
}

// Zero reports the step a connect attempt hung on at info/debug, so the buffer
// takes every level while the console keeps the quieter production threshold.
export function createZeroLogSink({
	consoleLevel = "warn",
	buffer = createZeroLogBuffer(),
	target,
	now,
}: {
	consoleLevel?: ZeroLogLevel
	buffer?: ZeroLogBuffer
	target?: ZeroConsoleLike
	now?: () => number
} = {}): { sink: ZeroLogSink; buffer: ZeroLogBuffer } {
	const sink: ZeroLogSink = {
		log: (level, context, ...args) => {
			buffer.push(level, args, now?.())
			if (LEVEL_RANK[level] < LEVEL_RANK[consoleLevel]) return
			const out = target ?? defaultConsole()
			out[level](...(context ? [...args, context] : args))
		},
	}
	return { sink, buffer }
}
